import { put, select, takeLatest } from '@redux-saga/core/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { gridSettingsProps } from '../../components/MyGridCore/types';
import { apiCall, APIValidationError } from '../api';
import { Await, Result } from '../api.d';
import tableSlice from './slice';
import {
  CustomColumnsUpdate,
  getAllSettingsResponse,
  patchSettingsResponse,
  TableSaveAsSettingsDTO,
  TableDeleteSettingsDTO,
  TablePatchSettingsDTO,
  TableState,
  TableSwitchFilterDTO,
  TableSaveSettingsDTO,
  NotifyDTO,
  CustomColumnsDataGet,
  CustomColumnsDataGetOk,
  LoadSettingsDTO,
} from './types';
import { baseURL } from '../root-saga';
import errorSlice from '../error/slice';
import { selectTable } from '../selectors';
import i18next from '../../i18n';
import { TableSettings } from '../../components/MyGridRedux/types';
import { addSearchParams } from '../../helpers/download';
import { TableFriendlyName } from '../info/types';
import { serializeSettings, unserializeSettings } from './helpers';

const getAllSettings = async (
  data: TableFriendlyName | undefined,
): Promise<Result<getAllSettingsResponse, APIValidationError>> => {
  const init = {
    method: 'GET',
    auth: true,
    role: true,
  };

  let url = new URL(`${baseURL}/api/tables`);
  url = addSearchParams(url, data ? { friendlyName: data } : undefined);

  return apiCall<getAllSettingsResponse>(url.toString(), init);
};

function* getAllSettingsSaga(action: PayloadAction<TableFriendlyName | undefined>) {
  const result = (yield getAllSettings(action.payload)) as Await<ReturnType<typeof getAllSettings>>;

  switch (result.type) {
    case 'ok':
      yield put(tableSlice.actions['ALL_SETTINGS:GET:OK'](result.value.data));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const patchSettings = async (
  gridSettings: gridSettingsProps,
  friendlyName: string,
): Promise<Result<patchSettingsResponse, APIValidationError>> => {
  const body = { table_settings: gridSettings };

  const init = {
    method: 'PATCH',
    auth: true,
    role: true,
    body: JSON.stringify(body),
  };

  return apiCall<patchSettingsResponse>(`${baseURL}/api/tables/${friendlyName}`, init);
};

function* patchSettingsSaga(action: PayloadAction<TablePatchSettingsDTO>) {
  const { gridSettings, friendlyName, fromExternal } = action.payload;

  const tableState: TableState = yield select(selectTable);
  const tableSetting = tableState.tablesSettings[friendlyName];

  /* Avoid API call when the part that changed is related with pagination */
  const currentDs = tableSetting.gridSettings.dataState;
  const newDs = gridSettings.dataState;
  const paginationChanged = currentDs.skip != newDs.skip || currentDs.take != newDs.take;
  if (paginationChanged) {
    yield put(
      tableSlice.actions['SETTINGS:PATCH:OK']({ tableSettings: { ...tableSetting, gridSettings: gridSettings } }),
    );
    return;
  }

  /* Do network request */
  const result = (yield patchSettings(serializeSettings(gridSettings), friendlyName)) as Await<
    ReturnType<typeof patchSettings>
  >;

  switch (result.type) {
    case 'ok':
      /* Update state again. TODO This could be avoided but some logic must be moved to frontend before,
       * in particular, the computation for columns_changed, filters_changed and has_filters fields.
       */
      //const tableSettings = unserializeSettings(result.value.data, { skip: gridSettings.dataState.skip });
      const { columns_changed, filters_changed, has_filters } = result.value.data;

      // Increment externalChangeCount if dataState has changed. This will force grid to re-sync
      let { externalChangeCount } = tableSetting;
      if (fromExternal && tableSetting.gridSettings.dataState != gridSettings.dataState) {
        externalChangeCount += 1;
      }
      yield put(
        tableSlice.actions['SETTINGS:PATCH:OK']({
          tableSettings: {
            ...tableSetting,
            gridSettings: gridSettings,
            columns_changed,
            filters_changed,
            has_filters,
            externalChangeCount,
          },
        }),
      );
      // Run callback. This is mostly used to refresh table data
      action.payload.onSuccess?.();
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const saveAsSettings = async (
  data: TableSaveAsSettingsDTO,
): Promise<Result<getAllSettingsResponse, APIValidationError>> => {
  const { friendlyName, ...rest } = data;

  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify({ name: rest.name, table_settings: rest.gridSettings }),
  };

  return apiCall<getAllSettingsResponse>(`${baseURL}/api/tables/${friendlyName}/saveAs`, init);
};

function* saveAsSettingsSaga(action: PayloadAction<TableSaveAsSettingsDTO>) {
  const { gridSettings } = action.payload;
  const data = { ...action.payload, gridSettings: serializeSettings(gridSettings) };
  const result = (yield saveAsSettings(data)) as Await<ReturnType<typeof saveAsSettings>>;

  switch (result.type) {
    case 'ok':
      yield put(tableSlice.actions['ALL_SETTINGS:GET:OK'](result.value.data));
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({
          message: i18next.t(`View saved.`),
          severity: 'success',
        }),
      );
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const saveSettings = async (data: {
  gridSettings: gridSettingsProps;
  friendlyName: TableFriendlyName;
}): Promise<Result<getAllSettingsResponse, APIValidationError>> => {
  const { friendlyName, ...rest } = data;

  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify({ table_settings: rest.gridSettings }),
  };

  return apiCall<getAllSettingsResponse>(`${baseURL}/api/tables/${friendlyName}/save`, init);
};

function* saveSettingsSaga(action: PayloadAction<TableSaveSettingsDTO>) {
  const { friendlyName } = action.payload;
  const tableState: TableState = yield select(selectTable);
  const { gridSettings } = tableState.tablesSettings[friendlyName];
  const data = { friendlyName, gridSettings: serializeSettings(gridSettings) };

  const result = (yield saveSettings(data)) as Await<ReturnType<typeof saveSettings>>;

  switch (result.type) {
    case 'ok':
      yield put(tableSlice.actions['ALL_SETTINGS:GET:OK']({ ...result.value.data }));
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({
          message: i18next.t(`View saved.`),
          severity: 'success',
        }),
      );
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

function* switchFilterSaga(action: PayloadAction<TableSwitchFilterDTO>) {
  const { friendlyName } = action.payload;
  const tableState: TableState = yield select(selectTable);
  const gridSettings: gridSettingsProps = tableState.tablesSettings[friendlyName].gridSettings;

  yield put(
    tableSlice.actions['SETTINGS:PATCH']({
      gridSettings: { ...gridSettings, advancedFilter: !gridSettings.advancedFilter },
      friendlyName,
    }),
  );
}

const loadSettings = async (
  personalSettingsId: number,
): Promise<Result<{ data: TableSettings }, APIValidationError>> => {
  const init = {
    method: 'POST',
    auth: true,
    role: true,
  };

  return apiCall<{ data: TableSettings }>(`${baseURL}/api/tables/${personalSettingsId}/load`, init);
};

function* loadSettingsSaga(action: PayloadAction<LoadSettingsDTO>) {
  const { personalSettingsId, onSuccess } = action.payload;
  const result = (yield loadSettings(personalSettingsId)) as Await<ReturnType<typeof loadSettings>>;

  switch (result.type) {
    case 'ok':
      const tableSettings = unserializeSettings(result.value.data);
      const state: TableState = yield select(selectTable);
      const { externalChangeCount } = state.tablesSettings[tableSettings.friendlyName];
      yield put(
        tableSlice.actions['SETTINGS:LOAD:OK']({
          tableSettings: { ...tableSettings, externalChangeCount: externalChangeCount + 1 },
        }),
      );
      onSuccess?.();
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const deleteSettings = async (
  data: TableDeleteSettingsDTO,
): Promise<Result<getAllSettingsResponse, APIValidationError>> => {
  const { friendlyName } = data;
  const init = {
    method: 'DELETE',
    auth: true,
    role: true,
  };

  return apiCall<getAllSettingsResponse>(`${baseURL}/api/tables/${friendlyName}`, init);
};

function* deleteSettingsSaga(action: PayloadAction<TableDeleteSettingsDTO>) {
  const result = (yield deleteSettings(action.payload)) as Await<ReturnType<typeof deleteSettings>>;

  switch (result.type) {
    case 'ok':
      yield put(tableSlice.actions['ALL_SETTINGS:GET:OK']({ ...result.value.data }));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const updateCustomColumns = async (
  data: CustomColumnsUpdate,
): Promise<Result<getAllSettingsResponse, APIValidationError>> => {
  const { friendlyName, customColumns } = data;
  const init = {
    method: 'PUT',
    auth: true,
    role: true,
    body: JSON.stringify({ custom_columns: customColumns }),
  };

  return apiCall<getAllSettingsResponse>(`${baseURL}/api/tables/${friendlyName}/custom-columns`, init);
};

function* updateCustomColumnsSaga(action: PayloadAction<CustomColumnsUpdate>) {
  const result = (yield updateCustomColumns(action.payload)) as Await<ReturnType<typeof updateCustomColumns>>;
  switch (result.type) {
    case 'ok':
      // We need to refresh table settings definitions.
      yield put(tableSlice.actions['ALL_SETTINGS:GET:OK']({ ...result.value.data }));

      // Force table to sync filters after custom columns update
      yield put(tableSlice.actions['EXTERNAL_CHANGE_COUNT:INCREMENT'](action.payload.friendlyName));

      // Run callback. This is mostly used to refresh table data
      action.payload.onSuccess?.();
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({
          message: i18next.t(`Aggregate columns updated`),
          severity: 'success',
        }),
      );
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const getCustomColumnsData = async (
  data: CustomColumnsDataGet,
): Promise<Result<CustomColumnsDataGetOk, APIValidationError>> => {
  const { tableFriendlyName, queryParams } = data;
  const init = {
    method: 'GET',
    auth: true,
    role: true,
  };

  let url = new URL(`${baseURL}/api/tables/${tableFriendlyName}/custom-columns/data`);
  url = addSearchParams(url, queryParams);

  return apiCall<CustomColumnsDataGetOk>(url.toString(), init);
};

function* getCustomColumnsDataSaga(action: PayloadAction<CustomColumnsDataGet>) {
  const result = (yield getCustomColumnsData(action.payload)) as Await<ReturnType<typeof getCustomColumnsData>>;
  switch (result.type) {
    case 'ok':
      const { customColumnsData } = result.value.data;
      const { tableFriendlyName } = action.payload;
      yield put(
        tableSlice.actions['CUSTOMCOLUMNS:DATA:GET:OK']({ friendlyName: tableFriendlyName, byId: customColumnsData }),
      );
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const notify = async (data: number[]): Promise<Result<{ data: any }, APIValidationError>> => {
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify({ ids: data }),
  };
  return apiCall<{ data: any }>(`${baseURL}/api/assignments/notify`, init);
};

function* notifySaga(action: PayloadAction<NotifyDTO>) {
  const { assignment_id } = action.payload;
  let ids: number[];
  const state: TableState = yield select(selectTable);
  if (assignment_id) {
    ids = [assignment_id];
  } else {
    ids = Object.keys(state.notifiableIds).map((id) => parseInt(id));
  }

  const result = (yield notify(ids)) as Await<ReturnType<typeof notify>>;
  switch (result.type) {
    case 'ok':
      for (let i = 0; i < ids.length; i++) {
        const assignment_id = ids[i];
        yield put(
          tableSlice.actions['NOTIFIABLES:UPDATE']({
            assignment_id,
            assigned: true,
          }),
        );
      }
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

export default [
  takeLatest(tableSlice.actions['ALL_SETTINGS:GET'].type, getAllSettingsSaga),
  takeLatest(tableSlice.actions['SETTINGS:PATCH'].type, patchSettingsSaga),
  takeLatest(tableSlice.actions.SWITCH_FILTER.type, switchFilterSaga),
  takeLatest(tableSlice.actions['SETTINGS:SAVE'].type, saveSettingsSaga),
  takeLatest(tableSlice.actions['SETTINGS:SAVE_AS'].type, saveAsSettingsSaga),
  takeLatest(tableSlice.actions['SETTINGS:LOAD'].type, loadSettingsSaga),
  takeLatest(tableSlice.actions['SETTINGS:DELETE'].type, deleteSettingsSaga),
  takeLatest(tableSlice.actions['CUSTOMCOLUMNS:UPDATE'].type, updateCustomColumnsSaga),
  takeLatest(tableSlice.actions['CUSTOMCOLUMNS:DATA:GET'].type, getCustomColumnsDataSaga),
  takeLatest(tableSlice.actions['NOTIFIABLES:NOTIFY'], notifySaga),
];
